import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  HomeIcon, 
  UsersIcon, 
  UserGroupIcon, 
  ClipboardDocumentListIcon,
  PhoneIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
  TagIcon,
  RectangleStackIcon,
  FlagIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';

const Layout = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const { usage, companyData, getStorageUsagePercentage, formatStorageSize, getSubscriptionStatus } = useCompany();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Navigation items based on user role
  const getNavigationItems = () => {
    const baseItems = [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, current: location.pathname === '/dashboard' },
    ];

    // Check if subscription is inactive (not active)
    const subscriptionStatus = getSubscriptionStatus();
    const isSubscriptionInactive = subscriptionStatus !== 'active' && subscriptionStatus !== 'trial';

    if (hasRole('super_admin')) {
      return [
        ...baseItems,
        { name: 'Plans', href: '/plans', icon: DocumentTextIcon, current: location.pathname === '/plans' },
        { name: 'Companies', href: '/companies', icon: BuildingOfficeIcon, current: location.pathname === '/companies' },
        { name: 'Orphaned Data', href: '/orphaned-data', icon: ExclamationTriangleIcon, current: location.pathname === '/orphaned-data' },
        { name: 'Industries', href: '/industries', icon: TagIcon, current: location.pathname === '/industries' },
        { name: 'Countries', href: '/countries', icon: GlobeAltIcon, current: location.pathname === '/countries' },
        { name: 'Currencies', href: '/currencies', icon: CurrencyDollarIcon, current: location.pathname === '/currencies' },
        { name: 'Analytics', href: '/analytics', icon: ChartBarIcon, current: location.pathname === '/analytics' },
        { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, current: location.pathname === '/settings' },
      ];
    }

    if (hasRole('company_admin')) {
      const adminItems = [
        ...baseItems,
        { name: 'User Management', href: '/user-management', icon: UsersIcon, current: location.pathname === '/user-management' },
        { name: 'Team Analytics', href: '/team-analytics', icon: ChartBarIcon, current: location.pathname === '/team-analytics' },
        { name: 'Performance Goals', href: '/performance-goals', icon: FlagIcon, current: location.pathname === '/performance-goals' },
        { name: 'Leads', href: '/leads', icon: UserGroupIcon, current: location.pathname === '/leads' },
        { name: 'Customers', href: '/customers', icon: UsersIcon, current: location.pathname === '/customers' },
        { name: 'Lead Stages', href: '/lead-stages', icon: RectangleStackIcon, current: location.pathname === '/lead-stages' },
        { name: 'Company Settings', href: '/company-settings', icon: Cog6ToothIcon, current: location.pathname === '/company-settings' },
      ];

      // For inactive subscriptions, only show dashboard
      if (isSubscriptionInactive) {
        return baseItems;
      }

      return adminItems;
    }

    // Regular user navigation
    const userItems = [
      ...baseItems,
      { name: 'Leads', href: '/leads', icon: UserGroupIcon, current: location.pathname === '/leads' },
      { name: 'Customers', href: '/customers', icon: UsersIcon, current: location.pathname === '/customers' },
      { name: 'My Analytics', href: '/user-analytics', icon: ChartBarIcon, current: location.pathname === '/user-analytics' },
    ];

    // For inactive subscriptions, only show dashboard for regular users too
    if (isSubscriptionInactive) {
      return baseItems;
    }

    return userItems;
  };

  const navigation = getNavigationItems();

  const StorageIndicator = () => {
    if (!usage || !companyData?.plan) return null;

    const percentage = getStorageUsagePercentage();
    const isNearLimit = percentage > 80;
    const isOverLimit = percentage >= 100;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 mx-3 mb-4"
      >
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="text-slate-300 font-medium">Storage Used</span>
          <span className={`font-semibold ${isOverLimit ? 'text-red-400' : isNearLimit ? 'text-yellow-400' : 'text-green-400'}`}>
            {percentage.toFixed(0)}%
          </span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-2 mb-2">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(percentage, 100)}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className={`h-2 rounded-full transition-all duration-300 ${
              isOverLimit ? 'bg-gradient-to-r from-red-500 to-red-600' : isNearLimit ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' : 'bg-gradient-to-r from-green-500 to-green-600'
            }`}
          />
        </div>
        <div className="text-xs text-slate-400">
          {(usage?.totalStorage || 0)} / {formatStorageSize(companyData.plan.storageLimitMB)}
        </div>
        {isOverLimit && (
          <div className="text-xs text-red-400 mt-2 font-medium flex items-center">
            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
            Storage limit exceeded - Read-only mode
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Mobile sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex z-40 md:hidden"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-600 bg-opacity-75"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "tween", duration: 0.3 }}
              className="relative flex-1 flex flex-col max-w-xs w-full bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 border-r border-slate-700/50 shadow-2xl"
            >
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 hover:bg-slate-700/80 transition-colors duration-200"
                >
                  <XMarkIcon className="h-6 w-6 text-white" />
                </button>
              </div>
              <SidebarContent navigation={navigation} StorageIndicator={StorageIndicator} user={user} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent navigation={navigation} StorageIndicator={StorageIndicator} user={user} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top navigation */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            onClick={() => setSidebarOpen(true)}
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Search bar */}
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex items-center">
              <div className="w-full max-w-lg lg:max-w-xs">
                <label htmlFor="search" className="sr-only">
                  Search
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="search"
                    name="search"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Search leads, customers..."
                    type="search"
                  />
                </div>
              </div>
            </div>

            {/* Right side of top nav */}
            <div className="ml-4 flex items-center md:ml-6">
              {/* Notifications */}
              <button className="bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                <BellIcon className="h-6 w-6" />
              </button>

              {/* Profile dropdown */}
              <div className="ml-3 relative">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium">
                        {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                      </div>
                    </div>
                    <div className="ml-3 hidden sm:block">
                      <div className="text-base font-medium text-gray-800">
                        {user?.firstName} {user?.lastName}
                      </div>
                      <div className="text-sm font-medium text-gray-500 capitalize">
                        {user?.role?.replace('_', ' ')}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleLogout}
                    className="bg-white p-2 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    title="Logout"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <motion.main
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex-1 relative overflow-y-auto focus:outline-none"
        >
          {children}
        </motion.main>
      </div>
    </div>
  );
};

// Sidebar content component
const SidebarContent = ({ navigation, StorageIndicator, user }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());

  const toggleSubmenu = (itemName) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemName)) {
      newExpanded.delete(itemName);
    } else {
      newExpanded.add(itemName);
    }
    setExpandedItems(newExpanded);
  };

  return (
    <div className="flex flex-col h-0 flex-1 bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 border-r border-slate-700/50 shadow-2xl">
      {/* Logo Section */}
      <div className="flex-1 flex flex-col pt-6 pb-4 overflow-y-auto">
        <div className="flex items-center justify-center flex-shrink-0 px-6">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="relative"
          >
            <img
              src="/assets/logo.png"
              alt="Company Logo"
              className="h-12 w-20 md:h-16 md:w-28 lg:h-24 lg:w-44 object-contain rounded-xl"
            />
            <div className="absolute -top-1 -right-1 h-3 w-3 bg-green-400 rounded-full border-2 border-slate-900 animate-pulse"></div>
          </motion.div>
        </div>

        {/* Navigation */}
        <nav className="mt-2 flex-1 px-3 space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isExpanded = expandedItems.has(item.name);
            const isAnySubmenuActive = hasSubmenu && item.submenu.some(subItem => subItem.current);

            if (hasSubmenu) {
              return (
                <div key={item.name}>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => toggleSubmenu(item.name)}
                    className={`group w-full flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 ${
                      isAnySubmenuActive
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                        : 'text-slate-300 hover:bg-white/10 hover:text-white hover:shadow-md'
                    }`}
                  >
                    <Icon
                      className={`mr-3 flex-shrink-0 h-5 w-5 transition-colors duration-300 ${
                        isAnySubmenuActive ? 'text-white' : 'text-slate-400 group-hover:text-purple-400'
                      }`}
                    />
                    <span className="flex-1 text-left">{item.name}</span>
                    <motion.div
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="ml-2"
                    >
                      <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </motion.div>
                  </motion.button>
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="ml-8 space-y-1 mt-2">
                          {item.submenu.map((subItem) => {
                            const SubIcon = subItem.icon;
                            return (
                              <motion.div
                                key={subItem.name}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <Link
                                  to={subItem.href}
                                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                                    subItem.current
                                      ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30'
                                      : 'text-slate-400 hover:bg-white/5 hover:text-white'
                                  }`}
                                >
                                  <SubIcon
                                    className={`mr-3 flex-shrink-0 h-4 w-4 transition-colors duration-300 ${
                                      subItem.current ? 'text-purple-400' : 'text-slate-500 group-hover:text-purple-400'
                                    }`}
                                  />
                                  {subItem.name}
                                </Link>
                              </motion.div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }

            return (
              <motion.div
                key={item.name}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  to={item.href}
                  className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-300 ${
                    item.current
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                      : 'text-slate-300 hover:bg-white/10 hover:text-white hover:shadow-md'
                  }`}
                >
                  <Icon
                    className={`mr-3 flex-shrink-0 h-5 w-5 transition-colors duration-300 ${
                      item.current ? 'text-white' : 'text-slate-400 group-hover:text-purple-400'
                    }`}
                  />
                  {item.name}
                </Link>
              </motion.div>
            );
          })}
        </nav>

        {/* Storage indicator for company admin and users */}
        {(user?.role === 'company_admin' || user?.role === 'user') && (
          <div className="px-3 mb-4">
            <StorageIndicator />
          </div>
        )}
      </div>
    </div>
  );
};

export default Layout;